/**
 * Step 6 — health score and Settings → scores and flags. Hooks over the step-6 contract routes only:
 * GET /clients/{id}/health, GET /clients/{id}/health/history, GET + PATCH /settings/thresholds,
 * GET + PATCH /settings/flags. Both PATCHes send the shared `config_version` as If-Match.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FlagsResponse, PatchFlagsBody, PatchThresholdsBody, ThresholdsResponse } from '@amplispot/contracts';
import { call } from '../api';

export const hkeys6 = {
  health: (id: string) => ['health', id] as const,
  history: (id: string) => ['health', id, 'history'] as const,
  thresholds: ['settings', 'thresholds'] as const,
  flags: ['settings', 'flags'] as const,
};

export function useClientHealth(clientId: string, enabled = true) {
  return useQuery({ queryKey: hkeys6.health(clientId), queryFn: () => call('health.get', { params: { id: clientId }, query: {} }), enabled });
}

export function useHealthHistory(clientId: string, enabled = true) {
  return useQuery({ queryKey: hkeys6.history(clientId), queryFn: () => call('health.history', { params: { id: clientId }, query: {} }), enabled });
}

export function useThresholds(enabled = true) {
  return useQuery({ queryKey: hkeys6.thresholds, queryFn: () => call('settings.thresholds'), enabled });
}

export function useFlags(enabled = true) {
  return useQuery({ queryKey: hkeys6.flags, queryFn: () => call('settings.flags'), enabled });
}

/** Thresholds and flags share one config_version, so a change to either refreshes both (and every score). */
function useSettingsRefresh() {
  const qc = useQueryClient();
  return (d: ThresholdsResponse | FlagsResponse) => {
    void qc.invalidateQueries({ queryKey: ['settings'] });
    void qc.invalidateQueries({ queryKey: ['health'] });
    void qc.invalidateQueries({ queryKey: ['clients'] });
    // capture.auto_threshold / countdown_seconds live here too: the held list's confidence colours read them.
    void qc.invalidateQueries({ queryKey: ['capture', 'settings'] });
    return d;
  };
}

export function useUpdateThresholds() {
  const refresh = useSettingsRefresh();
  return useMutation({
    mutationFn: (v: { version: number; body: PatchThresholdsBody }) => call('settings.updateThresholds', { body: v.body, ifMatch: v.version }),
    onSuccess: refresh,
  });
}

export function useUpdateFlags() {
  const refresh = useSettingsRefresh();
  return useMutation({
    mutationFn: (v: { version: number; body: PatchFlagsBody }) => call('settings.updateFlags', { body: v.body, ifMatch: v.version }),
    onSuccess: refresh,
  });
}

/**
 * True when the reasons shown do not account for the score: the server leaves tone reasons out of `explain` for
 * callers without `toneReason.read` (domain health.ts) but keeps their points, so "Score 60, Watch" can arrive with
 * no lines, or with lines that add up to less. The score is clamp(100 + Σ penalty, 0, 100) over every reason.
 */
export function scoreHasUnshownReasons(h: { value: number; explain: readonly { penalty?: number }[] }): boolean {
  const sum = h.explain.reduce((a, r) => a + (r.penalty ?? 0), 0);
  return Math.min(100, Math.max(0, 100 + sum)) !== h.value;
}

/** The neutral line shown in place of (or after) the reasons when part of the score is not shown to this viewer. */
export const UNSHOWN_REASONS_LINE = 'Part of the score comes from signals only managers see.';
